import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../common/Modal';
import ModalAction from '../../common/ModalAction';
import InstrumentRow from '../../common/InstrumentRow';
import MidiPiano from '../../MidiPiano';
import MidiSummary, { MidiKeyRow } from '../../MidiSummary';
import ChordBarsTransport, { ChordBarsMixer } from '../../ChordBarsTransport';
import TempoControl from './TempoControl';
import useMidiPreview from '../../../hooks/useMidiPreview';
import useSpacePlay from '../../../hooks/useSpacePlay';
import { getMidiTransposition } from '../../../utils/midiJson';
import { getPartInstrumentId } from '../../../utils/midiInstruments';
import { getKeyAfterTranspose } from '../../../utils/autoTranspose';

import { ReactComponent as MelodyIcon } from '../../../icons/music-single-note.svg';
import { ReactComponent as PlayIcon } from '../../../icons/play-solid.svg';
import { ReactComponent as SlidersIcon } from '../../../icons/sliders.svg';
import { ReactComponent as StopIcon } from '../../../icons/square-rounded.svg';

import './PartMidiModal.scss';

const DEFAULT_BPM = 120;

const PartMidiModal = ({ isOpen, onClose, part, displayNumber, songKey, songOriginalKey, transposeSemitones }) => {
  const { t } = useTranslation();
  const midiJson = part?.midi;
  const instrumentId = getPartInstrumentId(midiJson);
  const [keyShift, setKeyShift] = useState(0);
  const [practiceBpm, setPracticeBpm] = useState(0);
  const [isMixerOpen, setIsMixerOpen] = useState(false);
  const semitones = getMidiTransposition(midiJson, songOriginalKey, transposeSemitones) + keyShift;
  const displayKey = getKeyAfterTranspose(songKey, keyShift) || songKey;
  const sourceBpm = midiJson?.bpm || DEFAULT_BPM;
  const bpm = practiceBpm || sourceBpm;
  const player = useMidiPreview(midiJson, instrumentId, isOpen, semitones, practiceBpm);
  useSpacePlay(player, isOpen);

  useEffect(() => {
    if (isOpen) {
      setKeyShift(0);
      setPracticeBpm(0);
    } else {
      setIsMixerOpen(false);
    }
  }, [isOpen, midiJson]);

  if (!part) return null;

  // Play sits in the header, the way every preview dialog carries it — within reach of the keyboard
  // and the key and tempo controls rather than under them. The levels drop down from beside it.
  const transport = midiJson ? (
    <>
      <ModalAction
        icon={player.isPlaying ? <StopIcon className="solid small" /> : <PlayIcon className="solid" />}
        label={player.playLabel}
        active={player.isPlaying}
        busy={player.playBusy}
        progress={player.playProgress}
        disabled={!player.canPlay}
        onClick={player.isPlaying ? player.stop : player.play}
      />
      <ModalAction
        icon={<SlidersIcon />}
        label={t('player.volume')}
        active={isMixerOpen}
        onClick={() => setIsMixerOpen((open) => !open)}
      />
    </>
  ) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t(`parts.${part.name}`, { defaultValue: part.name })} ${displayNumber}`}
      className="PartMidiModal"
      actions={transport}
      sheet={
        midiJson && (
          <ChordBarsMixer
            isOpen={isMixerOpen}
            onClose={() => setIsMixerOpen(false)}
            player={player}
            idPrefix="part-midi"
            instrumentIcon={<MelodyIcon className="stroke-icon" aria-hidden="true" />}
          />
        )
      }
    >
      <div className="midi-details">
        <div className="playback-controls">
          <div className="control">
            <span className="caption">{t('view.key')}</span>
            <MidiKeyRow
              songKey={displayKey}
              onTranspose={midiJson ? (delta) => setKeyShift((s) => s + delta) : undefined}
            />
          </div>

          {midiJson && (
            <div className="control">
              <span className="caption">{t('dialogs.midi.tempo')}</span>
              <TempoControl bpm={bpm} onChange={setPracticeBpm} />
            </div>
          )}
        </div>

        <MidiSummary midiJson={midiJson} duration={player.duration} />
      </div>

      {midiJson ? (
        <>
          <MidiPiano midiJson={player.playbackMidi} activeNotes={player.activeNotes} />
          <ChordBarsTransport player={player}>
            <InstrumentRow instrumentId={instrumentId} />
          </ChordBarsTransport>
        </>
      ) : (
        <div className="empty-state">
          <p>{t('dialogs.midi.noMidi')}</p>
        </div>
      )}
    </Modal>
  );
};

export default PartMidiModal;
